/**
 * Multi-relation field codec — converts between backend wire format
 * (array of objects or array of IDs) and UI format (string[]).
 *
 * The legacy API returns multi-relation data as arrays of objects:
 *   [{ id: 24, module: "Home page" }, { id: 19, module: "Business ideas" }]
 *
 * The UI works with flat string arrays: ["24", "19"]
 *
 * On serialize, emits the flat string array unchanged — the API accepts
 * flat arrays on write.
 *
 * @module
 */

/**
 * Creates a FieldCodec for multi-relation fields.
 *
 * @param {string} [valueProp] - Property name to extract from objects. Default: 'id'.
 * @returns {import('../../contracts/forms.js').FieldCodec}
 */
export function createMultiRelationCodec(valueProp = 'id') {
  return {
    /** @param {unknown} wireValue */
    parse(wireValue) {
      if (wireValue == null) return [];
      if (!Array.isArray(wireValue)) return [];
      return wireValue
        .map(item => {
          if (item == null) return null;
          // Already a string/number ID
          if (typeof item === 'string') return item;
          if (typeof item === 'number') return String(item);
          // Object — extract valueProp
          if (typeof item === 'object') {
            const val = /** @type {Record<string, unknown>} */ (item)[valueProp];
            return val != null ? String(val) : null;
          }
          return null;
        })
        .filter(v => v != null && v !== '');
    },

    /** @param {unknown} uiValue */
    serialize(uiValue) {
      // UI value is already string[] — pass through
      if (!Array.isArray(uiValue)) return [];
      return uiValue;
    },
  };
}
